import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle, Package } from 'lucide-react';
import { launchersApi } from '../../../../utils/api/launchers';
import LauncherCard from './LauncherCard';

/**
 * LauncherGrid - Displays available launchers filtered by machine capabilities
 * 
 * Props:
 * - machine: Selected machine object (with capabilities)
 * - spaceId: Space ID for filtering launchers (optional)
 * - onLaunch: (launcher, profile) => void
 * - onShowDetails: (launcher) => void
 */
const LauncherGrid = ({ machine, spaceId, onLaunch, onShowDetails }) => {
  const [launchers, setLaunchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch launchers on mount
  useEffect(() => {
    const fetchLaunchers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await launchersApi.list({ 
          withProfiles: true,
          spaceId 
        });
        
        setLaunchers(data.launchers || []);
      } catch (err) {
        console.error('Failed to fetch launchers:', err);
        setError('Failed to load launchers');
      } finally {
        setLoading(false);
      }
    };

    fetchLaunchers();
  }, [spaceId]);

  // Filter launchers based on machine capabilities
  const availableLaunchers = useMemo(() => {
    if (!machine?.capabilities || machine.capabilities.length === 0) {
      return [];
    }

    return launchers.filter(launcher => 
      launcher.isAvailableOn(machine.capabilities)
    );
  }, [launchers, machine]);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <Loader2 size={32} className="animate-spin mb-4" />
        <p>Loading launchers...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-400">
        <AlertCircle size={32} className="mb-4" />
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-4 text-sm underline hover:text-red-300"
        >
          Retry
        </button>
      </div>
    );
  }

  // No machine selected
  if (!machine) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <Package size={48} className="mb-4 opacity-50" />
        <p className="text-lg font-medium text-white mb-2">No Machine Selected</p>
        <p className="text-sm">Select a target machine to see available launchers</p>
      </div>
    );
  }

  // No launchers available
  if (availableLaunchers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <Package size={48} className="mb-4 opacity-50" />
        <p className="text-lg font-medium text-white mb-2">No Applications Available</p>
        <p className="text-sm text-center max-w-md">
          {launchers.length === 0 
            ? 'No launchers have been configured yet.'
            : `None of the ${launchers.length} configured applications are installed on ${machine.hostname}.`
          }
        </p>
      </div>
    );
  }

  // Render launcher grid
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {availableLaunchers.map(launcher => (
        <LauncherCard
          key={launcher.launcher_id}
          launcher={launcher}
          machineCapabilities={machine.capabilities}
          machine={machine}
          spaceId={spaceId}
          onLaunch={onLaunch}
          onShowDetails={onShowDetails}
        />
      ))}
    </div>
  );
};

export default LauncherGrid;




